import React, { useState, useCallback } from 'react';
import { Btn } from 'components/btn/Btn';
import { createStyles } from 'utils/createStyle';
import { OrganizationDetail } from 'common/api/crm/models/OrganizationDetail';
import { REACT_APP_LAUNCH_SCRIPWARE_URL } from 'common/env';
import { formatPhoneNumber } from 'utils';
import { useFirebaseConfigValues } from 'hooks/useFirebaseConfigValues';
import { Value } from 'firebase/remote-config';
import { appcuesSelectors } from './sectionConstants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faDownload, faCheck } from '@fortawesome/pro-regular-svg-icons';
import { colors } from 'styles/settings';
import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import { Root, Trigger, Content } from 'components/popover/Popover';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import QRCode from 'qrcode';
import { logGetCoordinatorInvite } from 'config/analytics';
import { getEnrollmentUrl, getEnrollmentUrlQR } from 'utils/getEnrollmentUrl';

const styles = createStyles({
    btnStyles: ['my-4 sm:m-0', { width: 'fit-content' }],
    infoContainer: [
        'w-full md:w-4/5 h-auto mb-5 rounded-xl p-6 pt-0',
        {
            boxShadow: '0 5px 10px rgba(0, 0, 0, 0.1)',
        },
    ],
    title: ['mt-5 mb-2 text-sm font-semibold'],
    blueStyle: 'text-brand text-md',
    popoverContent: ['text-grey-1 text-xs focus:outline-0 focus:transition-none'],
    resourceButton: [
        'font-semibold text-sm mt-2 py-3.5 px-5 text-brand text-left border border-brand rounded-xl w-full transition-shadow',
        {
            '&:disabled': {
                color: colors.success,
                borderColor: colors.white,
                pointerEvents: 'none',
            },
            '&:active': {
                transition: 'none',
                boxShadow: 'none',
            },
            '&:focus': {
                transition: 'none',
                boxShadow: 'none',
                outline: 'none',
            },
        },
    ],
});

const toBoolean = (v: Value) => v.asBoolean();

export const SideMenu = ({
    showScripWareBtn,
    organizationInfo,
}: {
    showScripWareBtn: boolean;
    organizationInfo?: OrganizationDetail;
}) => {
    const [showCode, setShowCode] = useState<boolean>(false);
    const [helperOpen, setHelperOpen] = useState<boolean>(false);
    const [isCopied, copyText] = useCopyToClipboard(5000);

    const showEnrollmentResources = useFirebaseConfigValues(
        'enable_coordinator_enrollment_code_resources_web',
        undefined,
        toBoolean
    );

    const { btnStyles, infoContainer, blueStyle, title, popoverContent } = styles;
    const enrollmentCodeEmail = useFirebaseConfigValues<string>(
        'enrollment_code_reset_email',
        '',
        (value: Value) => value.asString()
    );

    const goToScripware = () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).location.href = REACT_APP_LAUNCH_SCRIPWARE_URL;
    };

    const getEnrollmentCode = useCallback(
        (enrollmentCode: string) => {
            const charsToHide = enrollmentCode.length - 4;
            const hideString = `${'*'.repeat(charsToHide)}${enrollmentCode.slice(charsToHide)}`;
            return showCode ? enrollmentCode : hideString;
        },
        [showCode]
    );

    const onCopyCode = useCallback(
        (text: string) => {
            const urlWithCode = getEnrollmentUrl(text);
            copyText(urlWithCode);
            logGetCoordinatorInvite('code');
        },
        [copyText]
    );

    const onDownloadQrCode = async (text: string) => {
        const urlWithCode = getEnrollmentUrlQR(text);
        let dataUrl;
        try {
            dataUrl = await QRCode.toDataURL(urlWithCode, { width: 1000 });
        } catch (err) {
            console.error(err);
        }

        if (dataUrl) {
            logGetCoordinatorInvite('qr');
            const linkQrCode = document.createElement('a');
            linkQrCode.style.display = 'none';
            linkQrCode.href = dataUrl;
            linkQrCode.download = 'RR-QRCode.png';
            document.body.appendChild(linkQrCode);
            linkQrCode.click();
            document.body.removeChild(linkQrCode);
            window.URL.revokeObjectURL(dataUrl);
        }
    };

    const onOpenChangeHelper = (isOpen: boolean) => {
        setHelperOpen(isOpen);
    };

    const InfoContainer = () => {
        if (!organizationInfo) {
            return null;
        }
        const { name, phoneNumber, enrollmentCode } = organizationInfo;

        return (
            <div className={infoContainer}>
                <div className="border-b border-grey-light pb-6">
                    <p className={title}>{name}</p>

                    <p className={title}>Organization Phone Number</p>
                    <p className={blueStyle}>
                        {phoneNumber ? formatPhoneNumber(phoneNumber) : 'Not available'}
                    </p>
                </div>
                <p className={title}>Enrollment Code</p>
                <div className="w-4/5 sm:w-auto flex justify-between align-center">
                    <span className={blueStyle}>
                        {enrollmentCode ? getEnrollmentCode(enrollmentCode) : 'Not available'}
                    </span>
                    <button
                        className="text-brand underline text-sm"
                        onClick={() => setShowCode(!showCode)}
                    >
                        {showCode ? 'Hide' : 'Show'}
                    </button>
                </div>
                {showEnrollmentResources && (
                    <>
                        <div className="flex align-center mt-3">
                            <p className={title}>Enrollment Code Resources</p>
                            <Root open={helperOpen} onOpenChange={onOpenChangeHelper}>
                                <Trigger className="h-4 sm:mt-4 mt-6 ml-1 flex">
                                    <FontAwesomeIcon
                                        color={helperOpen ? colors.brandDark : colors.brand}
                                        className="sm:text-sm text-md"
                                        icon={faInfoCircle}
                                    />
                                </Trigger>
                                <Content
                                    align="end"
                                    alignOffset={-93}
                                    side="top"
                                    className={popoverContent}
                                    sideOffset={5}
                                >
                                    <div className="font-semibold pb-1">Copy Invite Link:</div>
                                    <p>
                                        Paste this unique link in emails, social media posts to
                                        private groups, texts, or direct messages to participants.
                                        Those who access the link can enroll directly in your
                                        program and start earning.
                                    </p>
                                    <br />
                                    <div className="font-semibold pb-1">Download QR Code:</div>
                                    <p>
                                        Add your custom QR code to flyers or other promotional
                                        materials for participants. Those who scan the code can
                                        enroll directly in your program and start earning.
                                    </p>
                                    <br />
                                    <div className="font-semibold pb-1">Note:</div>
                                    <p>
                                        Ensure that the invite link and QR code are only shared with
                                        participants associated with your organization. Sharing
                                        these resources on public social media pages or websites can
                                        result in unwanted or fraudulent enrollments.
                                    </p>
                                </Content>
                            </Root>
                        </div>

                        <div className="w-4/5 sm:w-auto flex flex-col item-center">
                            <button
                                disabled={isCopied}
                                className={styles.resourceButton}
                                onClick={() => onCopyCode(enrollmentCode!)}
                            >
                                <FontAwesomeIcon
                                    style={{ fontSize: '15px' }}
                                    icon={isCopied ? faCheck : faCopy}
                                />
                                <span className="ml-2">
                                    {isCopied ? 'Copied to clipboard' : 'Copy Invite Link'}
                                </span>
                            </button>
                            <button
                                className={styles.resourceButton}
                                onClick={() => onDownloadQrCode(enrollmentCode!)}
                            >
                                <FontAwesomeIcon style={{ fontSize: '15px' }} icon={faDownload} />
                                <span className="ml-2">Download QR Code</span>
                            </button>
                        </div>
                    </>
                )}

                <div className="border-t border-grey-light pt-3 mt-7">
                    <div className="flex flex-col items-center mt-4 text-xs bg-pale-blue py-4 px-3 rounded-lg">
                        <div>Need a new Enrollment Code?</div>
                        <div>Email us at</div>
                        <a className="text-sm" href={`mailto:${enrollmentCodeEmail}`}>
                            {enrollmentCodeEmail}
                        </a>
                        <div className="py-2">OR</div>
                        <div>contact Customer Support</div>
                        <a className="text-sm no-underline" href="tel:1-800-727-4715">
                            1-800-727-4715, option 3
                        </a>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={`${appcuesSelectors.sideMenu} flex flex-col`}>
            <p className="md:max-w-xs text-brand-dark text-3xl mb-5 font-light sm:font-normal">
                Coordinator Dashboard
            </p>

            <InfoContainer />

            {showScripWareBtn && (
                <Btn isSecondary onClick={goToScripware} className={btnStyles}>
                    Launch SCRIPWARE
                </Btn>
            )}
        </div>
    );
};
